/**
 * Reclaim Assistant - SPECTACULAR Edition
 * An immersive, animated guide that wows users with:
 * - Floating particles & ambient effects
 * - Live feature previews with mini-app demos
 * - Morphing avatar with personality
 * - Spotlight effects on real UI elements
 * - Celebration animations for milestones
 * - Smooth, delightful micro-interactions
 * - ✨ Sound effects for magical feel
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { 
  Sparkles, 
  X, 
  ChevronRight, 
  ChevronLeft,
  Home,
  Moon,
  Mic,
  Target,
  Users,
  Crown,
  Lock,
  Unlock,
  Heart,
  Flame,
  TrendingUp,
  Star,
  Zap,
  Sun,
  Play,
  Volume2,
  Brain,
  Wind,
  Eye,
  Shield
} from "lucide-react";

// ============================================
// SOUND EFFECTS - Delightful audio feedback
// ============================================
const createAudioContext = () => {
  if (typeof window !== 'undefined' && window.AudioContext) {
    return new (window.AudioContext || window.webkitAudioContext)();
  }
  return null;
};

// Sound generator using Web Audio API
const SoundEffects = {
  audioContext: null,
  
  init() {
    if (!this.audioContext) {
      this.audioContext = createAudioContext();
    }
    return this.audioContext;
  },
  
  // Gentle opening chime - ascending notes
  playOpen() {
    const ctx = this.init();
    if (!ctx) return;
    
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5 - major chord
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      gain.gain.setValueAtTime(0, now + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.15, now + i * 0.08 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.4);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now + i * 0.08);
      osc.stop(now + i * 0.08 + 0.5);
    });
  },
  
  // Soft close sound - single descending tone
  playClose() {
    const ctx = this.init();
    if (!ctx) return;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(523.25, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(392, ctx.currentTime + 0.15);
    
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  },
  
  // Celebration sound - happy arpeggio!
  playCelebration() {
    const ctx = this.init();
    if (!ctx) return;
    
    const now = ctx.currentTime;
    // C major arpeggio going up
    const notes = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98];
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'triangle';
      osc.frequency.value = freq;
      
      const startTime = now + i * 0.06;
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.12, startTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(startTime);
      osc.stop(startTime + 0.35);
    });
    
    // Final sparkle
    setTimeout(() => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 2093; // High C
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.6);
    }, 350);
  },
  
  // Soft click/tap
  playTap() {
    const ctx = this.init();
    if (!ctx) return;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.value = 800;
    
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  },
  
  // Progress/next step sound
  playProgress() {
    const ctx = this.init();
    if (!ctx) return;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(900, ctx.currentTime + 0.1);
    
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  },
  
  // Suggestion appear - gentle ping
  playSuggestion() {
    const ctx = this.init();
    if (!ctx) return;
    
    const notes = [783.99, 987.77]; // G5, B5
    const now = ctx.currentTime;
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      gain.gain.setValueAtTime(0, now + i * 0.1);
      gain.gain.linearRampToValueAtTime(0.1, now + i * 0.1 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.3);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now + i * 0.1);
      osc.stop(now + i * 0.1 + 0.35);
    });
  }
};

// ============================================
// PARTICLE SYSTEM - Floating ambient particles
// ============================================
const Particle = ({ delay, duration, size, color }) => (
  <motion.div
    className={`absolute rounded-full ${color}`}
    style={{ width: size, height: size }}
    initial={{ 
      opacity: 0, 
      scale: 0,
      x: Math.random() * 280,
      y: Math.random() * 400
    }}
    animate={{ 
      opacity: [0, 0.6, 0],
      scale: [0, 1, 0],
      y: [null, -100],
    }}
    transition={{ 
      duration, 
      delay, 
      repeat: Infinity,
      ease: "easeOut"
    }}
  />
);

const ParticleField = ({ count = 15, colors = ["bg-violet-400/30", "bg-purple-400/30", "bg-pink-400/20"] }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(count)].map((_, i) => (
      <Particle
        key={i}
        delay={i * 0.4}
        duration={3 + Math.random() * 2}
        size={4 + Math.random() * 6}
        color={colors[i % colors.length]}
      />
    ))}
  </div>
);

// ============================================
// ANIMATED AVATAR - Morphing guide character
// ============================================
const AnimatedAvatar = ({ mood = "happy", color = "violet" }) => {
  const colorMap = {
    violet: "from-violet-500 to-purple-600",
    emerald: "from-emerald-500 to-teal-600",
    amber: "from-amber-500 to-orange-600",
    cyan: "from-cyan-500 to-blue-600",
    pink: "from-pink-500 to-rose-600"
  };

  return (
    <motion.div 
      className={`relative w-12 h-12 rounded-2xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center overflow-hidden`}
      animate={{ 
        borderRadius: ["30%", "40%", "30%"],
        rotate: [0, 2, -2, 0]
      }}
      transition={{ 
        duration: 4, 
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {/* Glow effect */}
      <motion.div 
        className="absolute inset-0 bg-white/20"
        animate={{ opacity: [0.1, 0.3, 0.1] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      
      {/* Face */}
      <div className="relative">
        {/* Eyes */}
        <div className="flex gap-1.5 mb-1">
          <motion.div 
            className="w-1.5 h-1.5 rounded-full bg-white"
            animate={{ scaleY: [1, 0.2, 1] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
          />
          <motion.div 
            className="w-1.5 h-1.5 rounded-full bg-white"
            animate={{ scaleY: [1, 0.2, 1] }}
            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
          />
        </div>
        {/* Smile */}
        <motion.div 
          className="w-3 h-1.5 border-b-2 border-white rounded-b-full mx-auto"
          animate={{ scaleX: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>
      
      {/* Sparkle */}
      <motion.div
        className="absolute -top-1 -right-1"
        animate={{ rotate: 360, scale: [0.8, 1, 0.8] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        <Sparkles size={12} className="text-white/80" />
      </motion.div>
    </motion.div>
  );
};

// ============================================
// FEATURE PREVIEW CARDS - Mini demos
// ============================================
const FeaturePreview = ({ feature, isActive }) => {
  const previews = {
    resets: (
      <motion.div 
        className="grid grid-cols-2 gap-1.5 p-2"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ staggerChildren: 0.1 }}
      >
        {[
          { icon: Wind, label: "Breathe", color: "bg-cyan-500/20" },
          { icon: Eye, label: "Focus", color: "bg-violet-500/20" },
          { icon: Heart, label: "Calm", color: "bg-pink-500/20" },
          { icon: Zap, label: "Energy", color: "bg-amber-500/20" }
        ].map((reset, i) => (
          <motion.div
            key={reset.label}
            className={`${reset.color} rounded-lg p-2 flex flex-col items-center`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            whileHover={{ scale: 1.05 }}
          >
            <reset.icon size={16} className="text-white/80 mb-1" />
            <span className="text-[10px] text-white/60">{reset.label}</span>
          </motion.div>
        ))}
      </motion.div>
    ),
    
    suite: (
      <motion.div className="p-3 space-y-2">
        <motion.div 
          className="h-16 rounded-xl bg-gradient-to-r from-violet-500/30 to-purple-500/30 flex items-center justify-center relative overflow-hidden"
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 5, repeat: Infinity }}
        >
          <Moon className="text-white/60" size={24} />
          <motion.div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
            animate={{ x: [-200, 200] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
          />
        </motion.div>
        <div className="flex gap-1">
          {["Meditation", "Performance", "Recovery"].map((mode, i) => (
            <motion.div
              key={mode}
              className="flex-1 py-1 rounded-md bg-white/10 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
            >
              <span className="text-[9px] text-white/50">{mode}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>
    ),
    
    voice: (
      <motion.div className="p-3 flex flex-col items-center">
        <motion.div 
          className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/30 to-orange-500/30 flex items-center justify-center mb-2"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <Mic className="text-white/70" size={24} />
        </motion.div>
        {/* Sound wave animation */}
        <div className="flex gap-0.5 items-end h-4">
          {[...Array(7)].map((_, i) => (
            <motion.div
              key={i}
              className="w-1 bg-amber-400/60 rounded-full"
              animate={{ height: [4, 12 + Math.random() * 8, 4] }}
              transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
            />
          ))}
        </div>
      </motion.div>
    ),
    
    challenges: (
      <motion.div className="p-2 space-y-1.5">
        {[
          { day: 1, done: true },
          { day: 2, done: true },
          { day: 3, done: false, active: true },
          { day: 4, done: false },
          { day: 5, done: false },
          { day: 6, done: false },
          { day: 7, done: false }
        ].map((d, i) => (
          <motion.div
            key={d.day}
            className={`flex items-center gap-2 py-1 px-2 rounded-lg ${
              d.active ? "bg-emerald-500/20" : d.done ? "bg-white/5" : "bg-white/[0.02]"
            }`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <motion.div 
              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                d.done ? "bg-emerald-500" : d.active ? "bg-emerald-500/50" : "bg-white/20"
              }`}
              animate={d.active ? { scale: [1, 1.2, 1] } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            >
              {d.done && <span className="text-[8px]">✓</span>}
            </motion.div>
            <span className="text-[10px] text-white/50">Day {d.day}</span>
          </motion.div>
        ))}
      </motion.div>
    ),
    
    alignment: (
      <motion.div className="p-3 flex flex-col items-center">
        <motion.div 
          className="text-3xl mb-2"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          ✨
        </motion.div>
        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-emerald-500"
            initial={{ width: "30%" }}
            animate={{ width: "70%" }}
            transition={{ duration: 2, ease: "easeOut" }}
          />
        </div>
        <div className="flex justify-between w-full mt-1">
          <span className="text-[9px] text-white/40">Low</span>
          <span className="text-[9px] text-white/40">High</span>
        </div>
      </motion.div>
    )
  };

  return (
    <motion.div
      className="bg-black/30 rounded-xl border border-white/10 overflow-hidden"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: isActive ? 1 : 0, height: isActive ? "auto" : 0 }}
      transition={{ duration: 0.3 }}
    >
      {previews[feature]}
    </motion.div>
  );
};

// ============================================
// CONFETTI CELEBRATION
// ============================================
const Confetti = ({ trigger }) => {
  if (!trigger) return null;
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className={`absolute w-2 h-2 ${
            ["bg-violet-400", "bg-pink-400", "bg-amber-400", "bg-emerald-400", "bg-cyan-400"][i % 5]
          }`}
          style={{
            left: `${Math.random() * 100}%`,
            borderRadius: Math.random() > 0.5 ? "50%" : "0%",
            rotate: Math.random() * 360
          }}
          initial={{ y: -20, opacity: 1 }}
          animate={{ 
            y: 400, 
            opacity: 0,
            rotate: Math.random() * 720,
            x: (Math.random() - 0.5) * 100
          }}
          transition={{ 
            duration: 2 + Math.random(),
            delay: Math.random() * 0.5,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// TOUR STEPS with previews
// ============================================
const TOUR_STEPS = [
  {
    id: "welcome",
    title: "Meet Excelior",
    message: "I'm Excelior, your guide back to yourself. Let me show you how Reclaim can transform your state of mind.",
    icon: Sparkles,
    color: "violet",
    gradient: "from-violet-500 to-purple-500",
    preview: null
  },
  {
    id: "alignment",
    title: "Daily Alignment Check",
    message: "Start each day by checking in. Rate your energy, choose your desired state, then flow into a personalized session.",
    icon: Heart,
    color: "emerald",
    gradient: "from-emerald-500 to-teal-500",
    preview: "alignment",
    badge: "FREE"
  },
  {
    id: "quick-resets",
    title: "Quick Resets",
    message: "60-90 seconds to shift your state. Perfect before meetings, when stressed, or to boost energy. Tap any card to begin!",
    icon: Target,
    color: "cyan",
    gradient: "from-cyan-500 to-blue-500",
    preview: "resets",
    badge: "FREE"
  },
  {
    id: "private-suite",
    title: "Private Suite",
    message: "Your sanctuary for deep work. AI-crafted affirmations, ambient soundscapes, and breathing rituals tailored to your goals.",
    icon: Moon,
    color: "violet",
    gradient: "from-violet-500 to-indigo-500",
    preview: "suite",
    badge: "5 MIN FREE"
  },
  {
    id: "voice-studio",
    title: "Voice Studio",
    message: "The most powerful affirmations? Your own voice. Record once, and hear yourself guiding your transformation.",
    icon: Mic,
    color: "amber",
    gradient: "from-amber-500 to-orange-500",
    preview: "voice",
    badge: "PREMIUM"
  },
  {
    id: "challenges",
    title: "7-Day Challenges",
    message: "Structured growth paths. The Visibility Challenge is free — build confidence and presence over 7 days.",
    icon: Users,
    color: "pink",
    gradient: "from-pink-500 to-rose-500",
    preview: "challenges",
    badge: "1 FREE"
  },
  {
    id: "complete",
    title: "You're Ready!",
    message: "Explore freely. I'll check in gently as you grow. Just tap me whenever you need guidance. Let's begin! ✨",
    icon: Star,
    color: "violet",
    gradient: "from-violet-500 to-purple-500",
    preview: null,
    celebration: true
  }
];

// Smart suggestion rules
const SUGGESTION_RULES = [
  {
    id: "reset_streak",
    condition: (stats) => stats.resetsThisWeek >= 3 && !stats.hasTriedPrivateSuite,
    title: "Ready for deeper work?",
    message: "You've built a reset habit! The Private Suite takes it deeper.",
    cta: "Try Private Suite",
    route: "/private-suite",
    icon: Moon,
    color: "violet",
    gradient: "from-violet-500 to-purple-500",
    priority: 1
  },
  {
    id: "first_reset",
    condition: (stats) => stats.totalResets === 0,
    title: "Try a Quick Reset",
    message: "60 seconds can shift your entire day. Start here!",
    cta: "Begin Reset",
    route: "/networker/resets",
    icon: Zap,
    color: "cyan",
    gradient: "from-cyan-500 to-blue-500",
    priority: 2
  },
  {
    id: "alignment_streak",
    condition: (stats) => stats.alignmentStreak >= 5,
    title: "You're on fire! 🔥",
    getMessage: (stats) => `${stats.alignmentStreak} days straight! Your brain is literally rewiring.`,
    cta: "Keep Going",
    route: "/",
    icon: Flame,
    color: "amber",
    gradient: "from-orange-500 to-red-500",
    priority: 1
  },
  {
    id: "try_challenge",
    condition: (stats) => stats.totalResets >= 5 && !stats.hasStartedChallenge,
    title: "Level Up?",
    message: "Ready for 7 days of structured growth? It's free!",
    cta: "Start Challenge",
    route: "/networker/challenges",
    icon: Target,
    color: "emerald",
    gradient: "from-emerald-500 to-teal-500",
    priority: 2
  },
  {
    id: "record_voice",
    condition: (stats) => stats.privateSuiteSessions >= 2 && !stats.hasRecordedVoice,
    title: "Make it personal",
    message: "Hearing YOUR voice is 3x more powerful than any other.",
    cta: "Record Voice",
    route: "/voice-studio",
    icon: Mic,
    color: "amber",
    gradient: "from-amber-500 to-orange-500",
    priority: 2
  },
  {
    id: "morning",
    condition: () => {
      const hour = new Date().getHours();
      return hour >= 5 && hour < 10;
    },
    title: "Good morning! ☀️",
    message: "Set your intention for the day with a quick alignment.",
    cta: "Morning Reset",
    route: "/networker/resets",
    icon: Sun,
    color: "amber",
    gradient: "from-amber-400 to-orange-400",
    priority: 3
  },
  {
    id: "evening",
    condition: () => {
      const hour = new Date().getHours();
      return hour >= 20 && hour < 24;
    },
    title: "Wind down time",
    message: "Release the day. A calm suite session prepares you for deep sleep.",
    cta: "Evening Session",
    route: "/private-suite",
    icon: Moon,
    color: "violet",
    gradient: "from-indigo-500 to-purple-500",
    priority: 3
  },
  {
    id: "weekly_celebration",
    condition: (stats) => stats.totalSessions >= 7 && stats.totalSessions % 7 === 0,
    title: "Weekly Win! 🎉",
    getMessage: (stats) => `${stats.totalSessions} sessions! You're building something beautiful.`,
    cta: "Celebrate",
    route: "/",
    icon: Star,
    color: "pink",
    gradient: "from-pink-500 to-rose-500",
    priority: 1,
    celebration: true
  }
];

// Helper to get user stats
const getUserStats = () => {
  try {
    return {
      totalResets: parseInt(localStorage.getItem("reclaim_total_resets") || "0"),
      resetsThisWeek: parseInt(localStorage.getItem("reclaim_resets_this_week") || "0"),
      alignmentStreak: parseInt(localStorage.getItem("reclaim_alignment_streak") || "0"),
      totalSessions: parseInt(localStorage.getItem("reclaim_total_sessions") || "0"),
      privateSuiteSessions: parseInt(localStorage.getItem("reclaim_suite_sessions") || "0"),
      hasTriedPrivateSuite: localStorage.getItem("reclaim_tried_suite") === "true",
      hasStartedChallenge: localStorage.getItem("reclaim_started_challenge") === "true",
      hasRecordedVoice: localStorage.getItem("reclaim_recorded_voice") === "true",
      lastMode: localStorage.getItem("reclaim_last_mode") || null
    };
  } catch {
    return { totalResets: 0, resetsThisWeek: 0, alignmentStreak: 0, totalSessions: 0, privateSuiteSessions: 0, hasTriedPrivateSuite: false, hasStartedChallenge: false, hasRecordedVoice: false, lastMode: null };
  }
};

const getBestSuggestion = (stats) => {
  const matching = SUGGESTION_RULES
    .filter(rule => { try { return rule.condition(stats); } catch { return false; } })
    .sort((a, b) => a.priority - b.priority);
  return matching[0] || null;
};

// ============================================
// MAIN COMPONENT
// ============================================
const ReclaimAssistant = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour, setHasSeenTour] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [userStats, setUserStats] = useState(getUserStats());
  const [showCelebration, setShowCelebration] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const buttonControls = useAnimation();

  // Play sound helper (respects user preference)
  const playSound = useCallback((soundFn) => {
    if (soundEnabled) {
      try {
        soundFn();
      } catch (e) {
        // Silently fail if audio context not available
      }
    }
  }, [soundEnabled]);

  useEffect(() => {
    const seen = localStorage.getItem("reclaim_tour_completed");
    const soundPref = localStorage.getItem("reclaim_assistant_sound");

    if (soundPref === "false") setSoundEnabled(false);

    if (seen) {
      setHasSeenTour(true);
    }
    // DEMO MODE — Excelior stays a quiet node by default. No auto-pop.
    // (Originally: if no tour seen + token present, auto-opened after 2s.)

    const stats = getUserStats();
    setUserStats(stats);
    const newSuggestion = getBestSuggestion(stats);
    setSuggestion(newSuggestion);
  }, []);

  // Refresh suggestions periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const stats = getUserStats();
      setUserStats(stats);
      setSuggestion(getBestSuggestion(stats));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Toggle sound preference
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem("reclaim_assistant_sound", newValue.toString());
    if (newValue) {
      playSound(() => SoundEffects.playTap());
    }
  };

  // Button hover animation
  const handleButtonHover = () => {
    buttonControls.start({
      scale: [1, 1.1, 1],
      rotate: [0, 5, -5, 0],
      transition: { duration: 0.5 }
    });
  };

  // Open/close with sound
  const handleToggle = () => {
    if (isOpen) {
      playSound(() => SoundEffects.playClose());
      setIsOpen(false);
    } else {
      playSound(() => SoundEffects.playOpen());
      setIsOpen(true);
    }
  };

  const startTour = () => {
    playSound(() => SoundEffects.playTap());
    setIsTourActive(true);
    setCurrentStep(0);
  };

  const nextStep = () => {
    playSound(() => SoundEffects.playProgress());
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      if (TOUR_STEPS[currentStep + 1].celebration) {
        setShowCelebration(true);
        playSound(() => SoundEffects.playCelebration());
        setTimeout(() => setShowCelebration(false), 3000);
      }
    } else {
      completeTour();
    }
  };

  const prevStep = () => {
    playSound(() => SoundEffects.playTap());
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const completeTour = () => {
    localStorage.setItem("reclaim_tour_completed", "true");
    setHasSeenTour(true);
    setIsTourActive(false);
    setIsOpen(false);
    setShowCelebration(true);
    playSound(() => SoundEffects.playCelebration());
    setTimeout(() => setShowCelebration(false), 3000);
  };

  const skipTour = () => {
    playSound(() => SoundEffects.playClose());
    localStorage.setItem("reclaim_tour_completed", "true");
    setHasSeenTour(true);
    setIsTourActive(false);
    setIsOpen(false);
  };

  const handleSuggestionClick = (route) => {
    if (suggestion?.celebration) {
      setShowCelebration(true);
      playSound(() => SoundEffects.playCelebration());
      setTimeout(() => setShowCelebration(false), 3000);
    } else {
      playSound(() => SoundEffects.playTap());
    }
    onNavigate?.(route);
    setIsOpen(false);
  };

  const handleQuickNav = (route) => {
    playSound(() => SoundEffects.playTap());
    onNavigate?.(route);
    setIsOpen(false);
  };

  const currentTourStep = TOUR_STEPS[currentStep];
  const StepIcon = currentTourStep?.icon || Sparkles;

  if (/^\/(golf\/coach|atelier\/guide|gym\/guide|networker\/guide|foundations\/guide|lights\/guide|soulumo\/guide|inner-path\/guide)/.test(window.location.pathname)) return null;
  return (
    <>
      {/* Floating Button - Morphing orb */}
      <motion.button
        onClick={handleToggle}
        onHoverStart={handleButtonHover}
        animate={buttonControls}
        className="fixed bottom-24 right-4 z-[60]"
        data-testid="reclaim-assistant-btn"
      >
        <motion.div
          className={`w-11 h-11 rounded-full flex items-center justify-center relative overflow-hidden ${
            isOpen 
              ? "bg-white/10 backdrop-blur-xl border border-white/20" 
              : "bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500"
          }`}
          animate={!isOpen ? {
            boxShadow: [
              "0 0 20px rgba(139, 92, 246, 0.5)",
              "0 0 40px rgba(139, 92, 246, 0.8)",
              "0 0 20px rgba(139, 92, 246, 0.5)"
            ]
          } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {/* Animated gradient background */}
          {!isOpen && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-pink-500 via-violet-500 to-cyan-500 opacity-50"
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            />
          )}
          
          {isOpen ? (
            <X size={20} className="text-white relative z-10" />
          ) : (
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="relative z-10"
            >
              <Sparkles size={20} className="text-white" />
            </motion.div>
          )}
        </motion.div>
        
        {/* Pulse indicators */}
        {!isOpen && (
          <>
            <motion.span 
              className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${
                suggestion ? "bg-emerald-400" : !hasSeenTour ? "bg-amber-400" : "bg-transparent"
              }`}
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            {/* Ripple effect */}
            <motion.span 
              className="absolute inset-0 rounded-full border-2 border-violet-400"
              animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </>
        )}
      </motion.button>

      {/* Main Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="fixed bottom-28 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] max-h-[60vh] bg-[#0f0f1a]/95 backdrop-blur-2xl rounded-3xl border border-white/10 shadow-2xl overflow-hidden"
            data-testid="reclaim-assistant-panel"
          >
            {/* Particle background */}
            <ParticleField colors={[
              `bg-${currentTourStep?.color || "violet"}-400/20`,
              "bg-purple-400/20",
              "bg-pink-400/15"
            ]} />
            
            {/* Celebration confetti */}
            <Confetti trigger={showCelebration} />

            {/* Header with avatar */}
            <motion.div 
              className={`relative p-4 bg-gradient-to-r ${currentTourStep?.gradient || suggestion?.gradient || "from-violet-500 to-purple-500"}`}
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 10, repeat: Infinity }}
            >
              <div className="flex items-center gap-3">
                <AnimatedAvatar 
                  color={isTourActive ? currentTourStep?.color : (suggestion?.color || "violet")} 
                />
                <div className="flex-1">
                  <motion.h3 
                    className="text-white font-semibold text-sm"
                    key={isTourActive ? currentStep : "menu"}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {isTourActive 
                      ? currentTourStep.title 
                      : suggestion 
                        ? suggestion.title 
                        : "Hi, I'm Excelior ✨"}
                  </motion.h3>
                  <p className="text-white/70 text-xs">
                    {isTourActive 
                      ? `Step ${currentStep + 1} of ${TOUR_STEPS.length}` 
                      : suggestion 
                        ? "A gentle suggestion" 
                        : "Your guide back to yourself"}
                  </p>
                </div>
                {(currentTourStep?.badge && isTourActive) && (
                  <motion.span 
                    className={`px-2 py-1 rounded-full text-[10px] font-bold ${
                      currentTourStep.badge === "PREMIUM" 
                        ? "bg-amber-500/30 text-amber-300" 
                        : "bg-emerald-500/30 text-emerald-300"
                    }`}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring" }}
                  >
                    {currentTourStep.badge}
                  </motion.span>
                )}
              </div>
            </motion.div>

            {/* Content */}
            <div className="p-4 relative">
              {!isTourActive ? (
                // Menu state
                <motion.div 
                  className="space-y-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {/* Personalized Suggestion */}
                  {suggestion && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10"
                    >
                      <p className="text-white/80 text-sm mb-3">
                        {suggestion.getMessage 
                          ? suggestion.getMessage(userStats) 
                          : suggestion.message}
                      </p>
                      <div className="flex gap-2">
                        <motion.button
                          onClick={() => handleSuggestionClick(suggestion.route)}
                          className={`flex-1 py-2.5 rounded-xl text-white text-sm font-medium bg-gradient-to-r ${suggestion.gradient}`}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          data-testid="suggestion-cta"
                        >
                          {suggestion.cta}
                        </motion.button>
                        <motion.button
                          onClick={() => setSuggestion(null)}
                          className="px-4 py-2.5 rounded-xl bg-white/10 text-white/50 text-sm"
                          whileHover={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                        >
                          Later
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Tour button */}
                  <motion.button
                    onClick={startTour}
                    className="w-full p-3 rounded-2xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/20 text-left flex items-center gap-3 group"
                    whileHover={{ scale: 1.02, borderColor: "rgba(139, 92, 246, 0.4)" }}
                    data-testid="start-tour-btn"
                  >
                    <motion.div 
                      className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/30 to-purple-500/30 flex items-center justify-center"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    >
                      <Sparkles size={18} className="text-violet-300" />
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">
                        {hasSeenTour ? "Retake Tour" : "Quick Tour"}
                      </p>
                      <p className="text-white/40 text-xs">See what Reclaim can do</p>
                    </div>
                    <ChevronRight size={16} className="text-white/30 group-hover:text-white/60 transition-colors" />
                  </motion.button>

                  {/* Ask Michael — opens AI chat in Michael's voice */}
                  <motion.button
                    onClick={() => { setIsOpen(false); onNavigate?.('/excelior'); }}
                    className="w-full p-3 rounded-2xl text-left flex items-center gap-3 group"
                    style={{
                      background: "linear-gradient(135deg, rgba(203,161,83,0.10), rgba(203,161,83,0.04))",
                      border: "1px solid rgba(203,161,83,0.25)",
                    }}
                    whileHover={{ scale: 1.02, borderColor: "rgba(203,161,83,0.5)" }}
                    data-testid="open-excelior-chat"
                  >
                    <motion.div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: "rgba(203,161,83,0.18)" }}
                      animate={{ scale: [1, 1.04, 1] }}
                      transition={{ duration: 4, repeat: Infinity }}
                    >
                      <Sparkles size={18} style={{ color: "#F5EBC4" }} />
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">Ask Michael</p>
                      <p className="text-white/40 text-xs">Get an answer in his voice</p>
                    </div>
                    <ChevronRight size={16} className="text-white/30 group-hover:text-white/60 transition-colors" />
                  </motion.button>
                  
                  {/* Quick Access Grid */}
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { icon: Home, label: "Home", route: "/", color: "emerald" },
                      { icon: Moon, label: "Suite", route: "/private-suite", color: "violet" },
                      { icon: Target, label: "Resets", route: "/resets", color: "cyan" },
                      { icon: Mic, label: "Voice", route: "/voice-studio", color: "amber" }
                    ].map((item, i) => (
                      <motion.button
                        key={item.label}
                        onClick={() => handleQuickNav(item.route)}
                        className="p-2 rounded-xl bg-white/5 flex flex-col items-center gap-1"
                        whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <item.icon size={16} className={`text-${item.color}-400`} />
                        <span className="text-[10px] text-white/50">{item.label}</span>
                      </motion.button>
                    ))}
                  </div>
                  
                  {/* Stats if user has activity */}
                  {(userStats.totalResets > 0 || userStats.alignmentStreak > 0) && (
                    <motion.div 
                      className="flex justify-center gap-6 pt-2 border-t border-white/5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      {userStats.alignmentStreak > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Flame size={14} className="text-orange-400" />
                          <span className="text-xs text-white/60">{userStats.alignmentStreak} day streak</span>
                        </div>
                      )}
                      {userStats.totalResets > 0 && (
                        <div className="flex items-center gap-1.5">
                          <Zap size={14} className="text-cyan-400" />
                          <span className="text-xs text-white/60">{userStats.totalResets} resets</span>
                        </div>
                      )}
                    </motion.div>
                  )}
                  
                  {/* Free/Premium legend + Sound toggle */}
                  <div className="flex justify-between items-center text-[10px] text-white/30 pt-1">
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1">
                        <Unlock size={10} className="text-emerald-400/60" /> Free
                      </span>
                      <span className="flex items-center gap-1">
                        <Lock size={10} className="text-amber-400/60" /> Premium
                      </span>
                    </div>
                    <button 
                      onClick={toggleSound}
                      className={`flex items-center gap-1 transition-colors ${soundEnabled ? 'text-white/50 hover:text-white/70' : 'text-white/20'}`}
                      title={soundEnabled ? "Mute sounds" : "Enable sounds"}
                    >
                      <Volume2 size={12} className={soundEnabled ? "" : "opacity-50"} />
                      {!soundEnabled && <span className="text-[8px]">off</span>}
                    </button>
                  </div>
                </motion.div>
              ) : (
                // Tour state
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <p className="text-white/80 text-sm leading-relaxed mb-4">
                    {currentTourStep.message}
                  </p>
                  
                  {/* Feature Preview */}
                  {currentTourStep.preview && (
                    <FeaturePreview 
                      feature={currentTourStep.preview} 
                      isActive={true} 
                    />
                  )}
                  
                  {/* Progress dots */}
                  <div className="flex justify-center gap-1.5 my-4">
                    {TOUR_STEPS.map((_, idx) => (
                      <motion.div
                        key={idx}
                        className={`h-1.5 rounded-full transition-all ${
                          idx === currentStep 
                            ? `bg-gradient-to-r ${currentTourStep.gradient} w-6` 
                            : idx < currentStep 
                              ? "bg-white/40 w-1.5" 
                              : "bg-white/20 w-1.5"
                        }`}
                        animate={idx === currentStep ? { scale: [1, 1.1, 1] } : {}}
                        transition={{ duration: 1, repeat: Infinity }}
                      />
                    ))}
                  </div>
                  
                  {/* Navigation */}
                  <div className="flex gap-2">
                    {currentStep > 0 && (
                      <motion.button
                        onClick={prevStep}
                        className="flex-1 py-2.5 rounded-xl bg-white/10 text-white/70 text-sm font-medium flex items-center justify-center gap-1"
                        whileHover={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                      >
                        <ChevronLeft size={16} />
                        Back
                      </motion.button>
                    )}
                    <motion.button
                      onClick={nextStep}
                      className={`flex-1 py-2.5 rounded-xl text-white text-sm font-medium flex items-center justify-center gap-1 bg-gradient-to-r ${currentTourStep.gradient}`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid="tour-next-btn"
                    >
                      {currentStep === TOUR_STEPS.length - 1 ? "Let's Go! 🚀" : "Next"}
                      {currentStep < TOUR_STEPS.length - 1 && <ChevronRight size={16} />}
                    </motion.button>
                  </div>
                  
                  {currentStep < TOUR_STEPS.length - 1 && (
                    <motion.button
                      onClick={skipTour}
                      className="w-full mt-2 py-2 text-white/30 text-xs hover:text-white/50"
                      whileHover={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      Skip tour
                    </motion.button>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Export tracking helper
export const trackActivity = (activityType, data = {}) => {
  try {
    const today = new Date().toDateString();
    
    switch (activityType) {
      case "reset_completed":
        const totalResets = parseInt(localStorage.getItem("reclaim_total_resets") || "0") + 1;
        localStorage.setItem("reclaim_total_resets", totalResets.toString());
        
        const weekStart = localStorage.getItem("reclaim_week_start");
        const currentWeekStart = getWeekStart();
        if (weekStart !== currentWeekStart) {
          localStorage.setItem("reclaim_week_start", currentWeekStart);
          localStorage.setItem("reclaim_resets_this_week", "1");
        } else {
          const weeklyResets = parseInt(localStorage.getItem("reclaim_resets_this_week") || "0") + 1;
          localStorage.setItem("reclaim_resets_this_week", weeklyResets.toString());
        }
        break;
        
      case "alignment_completed":
        const lastAlignment = localStorage.getItem("reclaim_last_alignment_date");
        const yesterday = new Date(Date.now() - 86400000).toDateString();
        
        if (lastAlignment === yesterday) {
          const streak = parseInt(localStorage.getItem("reclaim_alignment_streak") || "0") + 1;
          localStorage.setItem("reclaim_alignment_streak", streak.toString());
        } else if (lastAlignment !== today) {
          localStorage.setItem("reclaim_alignment_streak", "1");
        }
        localStorage.setItem("reclaim_last_alignment_date", today);
        break;
        
      case "suite_session_started":
        const suiteSessions = parseInt(localStorage.getItem("reclaim_suite_sessions") || "0") + 1;
        localStorage.setItem("reclaim_suite_sessions", suiteSessions.toString());
        localStorage.setItem("reclaim_tried_suite", "true");
        if (data.mode) localStorage.setItem("reclaim_last_mode", data.mode);
        break;
        
      case "session_completed":
        const totalSessions = parseInt(localStorage.getItem("reclaim_total_sessions") || "0") + 1;
        localStorage.setItem("reclaim_total_sessions", totalSessions.toString());
        break;
        
      case "challenge_started":
        localStorage.setItem("reclaim_started_challenge", "true");
        break;
        
      case "voice_recorded":
        localStorage.setItem("reclaim_recorded_voice", "true");
        break;
    }
  } catch (e) {
    console.log("Track activity error:", e);
  }
};

const getWeekStart = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  return monday.toDateString();
};

export default ReclaimAssistant;
